<template>
  <div>
    <div v-if="automations && automations.length > 0" class="rules-grid">
      <v-card
        v-for="(rule, idx) in automations"
        :key="idx"
        class="rule-card modern-rule-card customBorderRadius"
        elevation="0"
      >
        <div class="rule-card-header">
          <div class="d-flex align-center">
            <div class="rule-icon-badge mr-3">
              <v-icon size="20" color="#FFFFFF">mdi-flash-auto</v-icon>
            </div>
            <div class="flex-grow-1">
              <div class="rule-status-badge">
                <v-icon size="12" color="#119f75" class="mr-1">mdi-circle</v-icon>
                <span>Active</span>
              </div>
            </div>
            <v-btn small icon class="delete-btn" @click="deleteRule(rule.id)">
              <v-icon size="20">mdi-delete-outline</v-icon>
            </v-btn>
          </div>
        </div>

        <v-divider></v-divider>

        <v-card-text class="pa-4">
          <div class="rule-section mb-3">
            <div class="rule-label">
              <v-icon size="16" class="mr-2" color="primary">mdi-calendar-clock</v-icon>
              <span>Event Trigger</span>
            </div>
            <div class="rule-content">
              <v-chip small color="primary" outlined class="mr-2">
                {{ rule.event }}
              </v-chip>
              <v-icon v-if="rule.event != 'contactCreated'" size="16" color="grey">
                mdi-arrow-right
              </v-icon>
              <v-chip v-if="rule.targetValue" small color="primary" class="ml-2">
                {{ rule.targetValue }}
              </v-chip>
            </div>
          </div>

          <div class="rule-section">
            <div class="rule-label">
              <v-icon size="16" class="mr-2" color="#119f75">mdi-lightning-bolt</v-icon>
              <span>Action</span>
            </div>
            <div class="rule-content">
              <v-chip small color="success" outlined class="mr-2">
                {{ rule.action }}
              </v-chip>
              <v-icon size="16" color="grey">mdi-arrow-right</v-icon>
              <v-chip
                v-if="rule.actionChange && rule.actionChange.hasOwnProperty('name')"
                small
                color="success"
                class="ml-2"
              >
                {{ rule.actionChange.name }}
              </v-chip>
              <v-chip
                v-else-if="rule.actionChange && rule.actionChange.hasOwnProperty('template')"
                small
                color="success"
                class="ml-2"
              >
                {{ rule.actionChange.template.name }}
              </v-chip>
              <v-chip
                v-else-if="rule.actionChange && rule.actionChange.hasOwnProperty('audio')"
                small
                color="success"
                class="ml-2"
              >
                {{ rule.actionChange.audio.fileName }}
              </v-chip>
              <v-chip
                v-else-if="rule.actionChange && rule.actionChange.url"
                small
                color="success"
                class="ml-2"
              >
                {{ rule.actionChange.url }}
              </v-chip>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <!-- Add New Rule Card -->
      <v-card
        class="rule-card add-rule-card customBorderRadius"
        elevation="0"
        @click="newRuleDialog = true"
      >
        <div class="add-rule-content">
          <div class="add-rule-icon-wrapper mb-3">
            <v-icon size="48" color="primary">mdi-plus-circle-outline</v-icon>
          </div>
          <h4 class="add-rule-title">Add New Rule</h4>
          <p class="add-rule-subtitle">Create automation workflow</p>
        </div>
      </v-card>
    </div>

    <!-- Empty State -->
    <div v-else class="rules-grid">
      <v-card class="rule-card empty-state-card customBorderRadius" elevation="0">
        <div class="empty-state-content">
          <v-icon size="64" color="grey lighten-1" class="mb-3">mdi-robot-outline</v-icon>
          <h4 class="mb-2 grey--text text--darken-1">No Automation Rules</h4>
          <p class="grey--text mb-0">Start automating your workflow</p>
        </div>
      </v-card>

      <!-- Add New Rule Card -->
      <v-card
        class="rule-card add-rule-card customBorderRadius"
        elevation="0"
        @click="newRuleDialog = true"
      >
        <div class="add-rule-content">
          <div class="add-rule-icon-wrapper mb-3">
            <v-icon size="48" color="primary">mdi-plus-circle-outline</v-icon>
          </div>
          <h4 class="add-rule-title">Add New Rule</h4>
          <p class="add-rule-subtitle">Create your first automation</p>
        </div>
      </v-card>
    </div>
    <v-dialog v-model="newRuleDialog" width="900" persistent>
      <v-card class="modern-dialog-card">
        <div class="dialog-header">
          <div class="d-flex align-center">
            <div class="dialog-icon-badge mr-3">
              <v-icon color="#FFFFFF">mdi-flash-auto</v-icon>
            </div>
            <div>
              <h3 class="dialog-title mb-0">Create Automation Rule</h3>
              <p class="dialog-subtitle mb-0">Define trigger events and automated actions</p>
            </div>
          </div>
          <v-btn icon color="white" @click="newRuleDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-divider></v-divider>

        <v-card-text class="pa-6">
          <!-- Event Trigger Section -->
          <div class="form-section mb-5">
            <div class="form-section-header mb-4">
              <v-icon color="primary" class="mr-2">mdi-calendar-clock</v-icon>
              <h4 class="section-title mb-0">Event Trigger</h4>
            </div>
            <v-row class="ma-0">
              <v-col cols="12" md="6" class="pa-2">
                <label class="field-label">When a</label>
                <v-autocomplete
                  v-model="selectedEvent"
                  label="Select Event Field"
                  :items="allowedFields"
                  auto-select-first
                  outlined
                  dense
                  hide-details="auto"
                  @change="
                    targetValue = '';
                    selectedAction = '';
                    targetList = '';
                  "
                />
              </v-col>
              <v-col v-if="selectedEvent == 'dispoChange'" cols="12" md="6" class="pa-2">
                <label class="field-label">Changes to</label>
                <v-combobox
                  v-model="targetValue"
                  label="Select Value"
                  :items="finalVal"
                  item-text="title"
                  outlined
                  dense
                  hide-details="auto"
                  item-value="id"
                  auto-select-first
                  @change="removeTargetDispo"
                />
              </v-col>
            </v-row>
          </div>
          <!-- Action Section -->
          <div class="form-section mb-5">
            <div class="form-section-header mb-4">
              <v-icon color="success" class="mr-2">mdi-lightning-bolt</v-icon>
              <h4 class="section-title mb-0">Action to Perform</h4>
            </div>
            <v-row class="ma-0">
              <v-col cols="12" md="6" class="pa-2">
                <label class="field-label">Perform</label>
                <v-autocomplete
                  v-model="selectedAction"
                  label="Select Action"
                  :items="actionsFields"
                  outlined
                  dense
                  hide-details="auto"
                  auto-select-first
                />
              </v-col>
              <v-col v-if="selectedAction == 'changeList'" cols="12" md="6" class="pa-2">
                <label class="field-label">Move to List</label>
                <v-autocomplete
                  v-model="targetList"
                  label="Select List"
                  :items="allowedLists"
                  outlined
                  dense
                  hide-details="auto"
                  item-text="name"
                  item-value="id"
                  auto-select-first
                >
                  <template v-if="allowedLists.length < 1" v-slot:no-data>
                    <div class="px-4">
                      You have only one list, Please add another
                    </div>
                  </template>

                  <template v-else v-slot:no-data>
                    <div class="px-4">No lists found</div>
                  </template>
                </v-autocomplete>
              </v-col>

              <v-col v-if="selectedAction == 'sendWhatsapp'" cols="12" md="6" class="pa-2">
                <label class="field-label">WhatsApp Template</label>
                <v-autocomplete
                  v-model="template"
                  label="Select Template"
                  :items="templates"
                  outlined
                  dense
                  hide-details="auto"
                  item-text="name"
                  item-value="id"
                  return-object
                  auto-select-first
                >
                  <template v-if="templates.length <= 0" v-slot:no-data>
                  <div class="px-4">
                    You have no templates, Please add a template
                  </div>
                </template>
              </v-autocomplete>
              </v-col>

              <v-col v-if="selectedAction == 'sendVoiceBroadcast'" cols="12" md="6" class="pa-2">
                <label class="field-label">Voice Broadcast Audio</label>
                <v-autocomplete
                  v-model="vbiAudio"
                  label="Select Audio"
                  :items="vbiAudios"
                  outlined
                  dense
                  hide-details="auto"
                  item-text="fileName"
                  item-value="id"
                  return-object
                  auto-select-first
                >
                  <template v-if="vbiAudios.length <= 0" v-slot:no-data>
                    <div class="px-4">
                      You have no audio, Please add a voice broadcast campaign for audio
                    </div>
                  </template>
                </v-autocomplete>
              </v-col>

              <v-col v-if="selectedAction == 'changeStage'" cols="12" md="6" class="pa-2">
                <label class="field-label">Change to Stage</label>
                <v-autocomplete
                  v-model="targetStage"
                  label="Select Stage"
                  :items="allowedStages"
                  outlined
                  dense
                  hide-details="auto"
                  item-text="name"
                  item-value="id"
                  auto-select-first
                />
              </v-col>

              <v-col v-if="selectedAction == 'callWebHook'" cols="12" md="6" class="pa-2">
                <label class="field-label">Webhook URL</label>
                <v-text-field
                  v-model="targetWebhook"
                  label="Enter Webhook URL"
                  outlined
                  dense
                  hide-details="auto"
                  placeholder="https://example.com/webhook"
                />
              </v-col>

              <v-col v-if="selectedAction == 'interaktEvent'" cols="12" md="6" class="pa-2">
                <label class="field-label">Interakt Event</label>
                <v-autocomplete
                  v-model="targetEventInterakt"
                  label="Select Event"
                  :items="allowedEvents"
                  outlined
                  dense
                  hide-details="auto"
                  item-text="name"
                  item-value="name"
                  auto-select-first
                />
              </v-col>

              <v-col v-if="selectedAction == 'notificationEvent'" cols="12" md="6" class="pa-2">
                <label class="field-label">Notify User</label>
                <v-autocomplete
                  v-model="targetEventNotification"
                  label="Select User"
                  :items="accounts"
                  outlined
                  dense
                  hide-details="auto"
                  item-text="name"
                  item-value="name"
                  auto-select-first
                  return-object
                >
                  <template v-if="accounts.length <= 0" v-slot:no-data>
                    <div class="px-4">
                      No users available
                    </div>
                  </template>
                </v-autocomplete>
              </v-col>

              <v-col v-if="targetList != ''" cols="12" md="6" class="pa-2">
                <label class="field-label">Update Disposition To</label>
                <v-combobox
                  v-model="changeDispoTo"
                  label="Select Value"
                  :items="targetDispose"
                  outlined
                  dense
                  hide-details="auto"
                  item-text="title"
                  item-value="id"
                  auto-select-first
                />
              </v-col>
            </v-row>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-6">
          <v-spacer></v-spacer>
          <v-btn large outlined color="grey darken-1" class="mr-3" @click="newRuleDialog = false">
            <v-icon left>mdi-close</v-icon>
            Cancel
          </v-btn>
          <v-btn large depressed color="primary" @click="saveRuleList">
            <v-icon left>mdi-content-save</v-icon>
            Save Rule
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import fb from "../js/firebase";
export default {
  props: {
    list: Object,
    allLists: Array,
    pageName: String,
  },
  data: () => ({
    vbiAudio: {},
    vbiAudios: [],
    automations: [],
    templates: [],
    targetEventWhatsapp: "",
    actions: [
      { text: "List Change", value: "changeList" },
      { text: "Stage Change", value: "changeStage" },
      { text: "Call Web Hook", value: "callWebHook" },
      { text: "Send Notification", value: "notificationEvent" },
      { text: "Send Whatsapp", value: "sendWhatsapp" },
      // { text: "Send Voice Broadcast", value: "sendBroadcast" },
    ],
    allowedKey: "",
    allowedStages: [],
    targetStage: "",
    allowedEvents: [],
    targetEventInterakt: "",
    targetEventNotification: "",
    targetWebhook: "",
    newRuleDialog: false,
    selectedEvent: "",
    targetValue: "",
    targetList: "",
    template: {},
    selectedAction: "",
    targetDispose: [],
    dispoField: [
      { text: "Contact's Disposition", value: "dispoChange" },
      { text: "Contact Is Created", value: "contactCreated" },
    ],
    changeDispoTo: {},
    interakt: {
      type: "interakt",
      config: {
        key: "",
        events: [],
      },
      events: [],
      companyId: "",
      enabled: false,
    },
  }),

  computed: {
    ...mapGetters([
      "selectedTeam",
      "ENDPOINT",
      "teams",
      "selectedList",
      "dispoList",
      "companyDetails",
    ]),
    allowedFields() {
      console.log("this.dispoField", this.dispoField);
      return this.dispoField;
    },
    actionsFields() {
      if (this.selectedEvent == "contactCreated") {
        return this.actions.filter(
          (item) => item.value === "notificationEvent"
        );
      } else if (this.selectedEvent == "dispoChange") {
        return this.actions.filter(
          (item) => item.value !== "notificationEvent"
        );
      } else {
        if (this.interakt.enabled) {
          return this.actions;
        } else {
          return this.actions.filter(
            (action) => action.value !== "interaktEvent"
          );
        }
      }
    },
    finalVal() {
      if (this.selectedEvent == "dispoChange") {
        return _.filter(this.dispoList, (d) => {
          return (
            d.title != "NEW" &&
            d.title != "SMS" &&
            d.title != "EMAIL" &&
            d.title != "LINK" &&
            d.title != "APP"
          );
        });
      } else if (this.selectedEvent == "assigneeChange") {
        return this.members;
      } else {
        return [];
      }
    },
    allowedLists() {
      return this.allLists.filter((f) => f != this.list);
    },
  },
  async created() {
    this.loadAutomations();

    this.setDispos();

    var listId = this.list.id;
    var response = await this.$http.get(
      `${this.ENDPOINT}/lists/${listId}/stages`
    );
    this.allowedStages = response.body;

    var resp = await this.$http.get(
      `${this.ENDPOINT}/integrations?filter[where][companyId]=${this.companyDetails.id}&filter[where][type]=interakt`
    );

    if (resp.body.length > 0) {
      this.interakt = resp.body[0];
      this.allowedEvents = resp.body[0].config.events;
      this.allowedKey = resp.body[0].config.key;
    }

    var resp2 = await this.$http.get(
      `${this.ENDPOINT}/integrations?filter[where][companyId]=${this.companyDetails.id}&filter[where][type]=whatsapp`
    );

    if (resp2.body.length > 0) {
      if (resp2.body[0].enabled == true) {
        if (this.pageName == "Whatsapp_Automation") {
          this.actions = [];
          this.dispoField = [];

          this.dispoField.push({
            text: "Contact's Disposition",
            value: "dispoChange",
          });
          this.actions.push({ text: "Send Whatsapp", value: "sendWhatsapp" });
        }

        this.getTemplates();
      }
    }

    // var respVB = await this.$http.get(
    //   `${this.ENDPOINT}/voiceBroadcasts?filter[where][companyId]=${this.companyDetails.id}&filter[include]=audio`
    // );

    // var dataVB = respVB.data;

    // this.vbiCampaigns = dataVB.filter((obj) => obj.audio && obj.audio.approved);

    // console.log("this.vbiCampaigns", JSON.stringify(this.vbiCampaigns));

    const respAudio = await this.$http.get(
      `${this.ENDPOINT}/audioLists?filter[where][companyId]=${this.companyDetails.id}`
    );
    console.log("Audio List:", respAudio.body);
    this.vbiAudios = respAudio.body.filter((audio) => audio.approved === true);

    if (
      this.companyDetails &&
      this.companyDetails.kycDetails &&
      this.companyDetails.kycDetails.kycVerified
    ) {
      if (this.vbiAudios.length > 0) {
        this.actions.push({
          text: "Send Voice Broadcast",
          value: "sendVoiceBroadcast",
        });
      }
    }

    this.getUsersList();
  },
  methods: {
    openDialog() {
      this.newRuleDialog = true;
    },
    async loadAutomations() {
      try {
        const response = await this.$http.get(
          `${this.ENDPOINT}/automations?filter[where][listId]=${this.list.id}`
        );
        this.automations = Array.isArray(response.body) ? response.body : [];
      } catch (e) {
        console.error("Error loading automations:", e);
        this.automations = [];
      }

      // Fallback to old-style triggers stored on list
      if (!this.automations || this.automations.length === 0) {
        try {
          const listResponse = await this.$http.get(
            `${this.ENDPOINT}/lists/${this.list.id}?filter[fields][triggers]=true`
          );
          const triggers = listResponse.body && listResponse.body.triggers;
          if (Array.isArray(triggers) && triggers.length > 0) {
            this.automations = triggers;
          }
        } catch (e) {
          console.error("Error loading list triggers fallback:", e);
        }
      }
    },
    getTeamIds() {
      var teams = [];
      if (this.selectedTeam == "allTeams") {
        teams = _.map(this.teams, "id");
      } else {
        teams.push(this.selectedTeam);
      }
      teams = _.without(teams, "allTeams");
      return teams;
    },
    /** Fetch the accounts based on teamsId */
    async getUsersList() {
      this.loader = true;
      this.accounts = [];
      // Teams sections
      var teams = this.getTeamIds();

      try {
        var response = await this.$http.post(
          `${this.$store.state.ENDPOINT}/accounts/fetch-v2`,
          {
            teams: teams,
          }
        );
        response = response.body;
        this.accounts = response;

        console.log("this.accounts", this.accounts);

        if (
          this.$store.getters.user.role == "Manager" ||
          this.$store.getters.user.role == "Teammanager"
        ) {
          this.accounts.unshift({ id: "allAccounts", name: "All Members" });
        }
        if (
          this.selectedAccount == "" &&
          this.accounts &&
          this.accounts.length > 0
        ) {
          return (this.selectedAccount = this.accounts[0].id);
        }
      } catch (e) {
        this.loader = false;
        this.$swal({ type: "error", text: "Something went wrong" });
      }
    },
    refresh() {
      console.log("Refresh in automation Rules");
      this.loadAutomations();
      this.$emit("refresh");
    },
    getTemplates() {
      this.preloader = true;
      this.templates = [];
      // Return the Promise chain
      return this.$http
        .get(this.$store.state.ENDPOINT + "/whatsapps/getTemplates")
        .then((response) => {
          this.preloader = false;
          response = response.body;
          if (response && response.data && Array.isArray(response.data)) {
            this.templates = _.filter(response.data, { status: "APPROVED" });
            console.log("Loaded templates:", this.templates);
          } else {
            // throw new Error(
            //   "Invalid response format while loading the templates."
            // );
          }
        })
        .catch((error) => {
          this.preloader = false;
          console.error("Failed to load templates:", error);
          this.$swal({
            type: "error",
            text:
              "Something went wrong while loading the templates: " +
              error.message,
          });
          throw error; // Rethrow to keep the error propagating
        })
        .finally(() => {
          this.preloader = false; // Ensure preloader is turned off
        });
    },
    setDispos() {
      // Set Target Dispos
      _.map(this.dispoList, (d) => {
        if (
          d.title != "SMS" &&
          d.title != "EMAIL" &&
          d.title != "LINK" &&
          d.title != "APP"
        ) {
          this.targetDispose.push({
            id: d.id,
            title: d.title,
          });
        }
      });

      console.log(" this.targetDispose", this.targetDispose);

      this.changeDispoTo = { id: "sameAs", title: "Same As Before" };
      this.targetDispose.unshift({ id: "rechurn", title: "Rechurn" });
      this.targetDispose.unshift(this.changeDispoTo);
    },
    removeTargetDispo(e) {
      this.targetDispose = _.filter(this.targetDispose, (d) => {
        return d.title != e.title;
      });
    },
    deleteRule(id) {
      this.$swal({
        title: "Are you sure?",
        text: "Remove this Rule?",
        type: "warning",
        showCancelButton: true,
        confirmButtonColor: "#1bca3f",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, Remove it!",
      })
        .then(async (willDelete) => {
          if (willDelete.value) {
            try {
              await this.$http.delete(`${this.ENDPOINT}/automations/${id}`);
              this.$swal({ type: "success", text: "Successful" });
              fb.log("remove_automation");
              this.refresh();
            } catch (err) {
              console.log("Error deleting rule:", err);
              this.$swal({ type: "error", text: err.body.error.message });
            }
          }
        })
        .catch((err) => {
          console.log(err);
        });
    },
    async saveRuleList() {
      const self = this;

      if (self.selectedEvent == "" || self.selectedAction == "") {
        return self.$swal({ type: "warning", text: "Please fill all fields" });
      }

      if (self.selectedEvent == "dispoChange" && self.targetValue == "") {
        self.$swal({
          type: "warning",
          text: `Please fill all fields`,
        });
        return;
      }

      if (self.selectedAction == "changeList" && self.targetList == "") {
        return self.$swal({ type: "warning", text: "Please select a list" });
      }

      if (self.selectedAction == "changeStage" && self.targetStage == "") {
        return self.$swal({ type: "warning", text: "Please select a stage" });
      }

      if (
        self.selectedAction == "interaktEvent" &&
        self.targetEventInterakt == ""
      ) {
        return self.$swal({ type: "warning", text: "Please select events" });
      }

      if (
        self.selectedAction == "notificationEvent" &&
        self.targetEventNotification == ""
      ) {
        return self.$swal({ type: "warning", text: "Please select user" });
      }

      var payload = { action: self.selectedAction };

      if (self.selectedEvent.substr(0, 5) == "field") {
        payload.event = "fieldChange";
        payload.targetField = self.selectedEvent.substr(
          self.selectedEvent.indexOf("=") + 1
        );
      } else {
        payload.event = self.selectedEvent;
      }

      if (typeof self.targetValue == "object") {
        payload.targetValue = self.targetValue.title;
      } else {
        payload.targetValue = self.targetValue;
      }

      if (self.changeDispoTo.id != "sameAs") {
        payload.changeDispoTo = self.changeDispoTo;
      }

      if (self.selectedAction == "changeList") {
        var res = await self.$http.get(
          `${self.ENDPOINT}/lists/${self.targetList}?filter[fields][id]=true&filter[fields][name]=true`
        );
        payload.actionChange = res.body;
      }

      if (self.selectedAction == "changeStage") {
        var resStage = await self.$http.get(
          `${self.ENDPOINT}/stages/${self.targetStage}?filter[fields][id]=true&filter[fields][name]=true`
        );
        payload.actionChange = resStage.body;
      }

      if (self.selectedAction == "interaktEvent") {
        payload.actionChange = {
          name: self.targetEventInterakt,
          key: self.allowedKey,
        };
      }

      if (self.selectedAction == "notificationEvent") {
        payload.actionChange = {
          name: self.targetEventNotification.name,
          key: self.targetEventNotification.id,
        };
      }

      function validURL(url) {
        var re = /^(http[s]?:\/\/){0,1}(www\.){0,1}[a-zA-Z0-9\.\-]+\.[a-zA-Z]{2,5}[\.]{0,1}/;
        if (!re.test(url)) {
          return false;
        }
        return true;
      }

      if (self.selectedAction == "callWebHook") {
        if (!validURL(self.targetWebhook)) {
          return self.$swal({
            type: "warning",
            text: "Please enter a valid URL",
          });
        } else {
          payload.actionChange = {
            id: self.targetWebhook,
            url: self.targetWebhook,
          };
        }
      }

      if (self.selectedAction == "sendWhatsapp") {
        payload.actionChange = {
          template: self.template,
          key: self.allowedKey,
        };
      }

      if (self.selectedAction == "sendVoiceBroadcast") {
        payload.actionChange = {
          audio: self.vbiAudio,
          key: self.$store.state.user.id,
        };
      }

      // Check for duplicate rules
      var ruleExists = this.automations.some((rule) => {
        return (
          rule.event === payload.event &&
          rule.targetValue === payload.targetValue &&
          rule.action === payload.action &&
          JSON.stringify(rule.actionChange) ===
            JSON.stringify(payload.actionChange)
        );
      });

      if (ruleExists) {
        return self.$swal({ text: "Rule Already Present", type: "warning" });
      }

      try {
        await self.$http.post(`${self.ENDPOINT}/automations`, {
          ...payload,
          listId: self.list.id,
        });
        fb.log("add_automation");
        this.$swal({ type: "success", text: "Automation Rule set" });
        self.newRuleDialog = false;
        this.refresh();
      } catch (e) {
        console.log("Error in saving automation rule", e);
      }
    },
  },
};
</script>

<style scoped>
/* Rules Grid Layout */
.rules-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
  padding-top: 10px;
}

/* Rule Card Styling */
.rule-card {
  border-radius: 12px !important;
  border: 1px solid #e0e0e0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.modern-rule-card {
  background: #ffffff;
}

.modern-rule-card:hover {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  transform: translateY(-4px);
}

.rule-card-header {
  background: linear-gradient(135deg, #37cf37 0%, #2ba82b 100%);
  padding: 16px;
}

.rule-icon-badge {
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
}

.rule-status-badge {
  display: inline-flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.25);
  border-radius: 12px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 600;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.4);
}

.delete-btn {
  background: rgba(255, 255, 255, 0.2) !important;
  transition: all 0.2s;
}

.delete-btn:hover {
  background: rgba(244, 67, 54, 0.9) !important;
}

.rule-section {
  margin-bottom: 16px;
}

.rule-section:last-child {
  margin-bottom: 0;
}

.rule-label {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  color: #616161;
  margin-bottom: 8px;
}

.rule-content {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

/* Add Rule Card */
.add-rule-card {
  border: 2px dashed #37cf37;
  background: rgba(55, 207, 55, 0.03);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 220px;
  transition: all 0.3s;
}

.add-rule-card:hover {
  background: rgba(55, 207, 55, 0.08);
  border-color: #2ba82b;
  transform: translateY(-4px);
  box-shadow: 0 4px 20px rgba(55, 207, 55, 0.2);
}

.add-rule-content {
  text-align: center;
  padding: 24px;
}

.add-rule-icon-wrapper {
  display: inline-block;
  transition: all 0.3s;
}

.add-rule-card:hover .add-rule-icon-wrapper {
  transform: scale(1.1) rotate(90deg);
}

.add-rule-title {
  font-size: 16px;
  font-weight: 600;
  color: #37cf37;
  margin-bottom: 4px;
}

.add-rule-subtitle {
  font-size: 13px;
  color: #757575;
  margin: 0;
}

/* Empty State Card */
.empty-state-card {
  border: 1px solid #e0e0e0;
  background: #fafafa;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 220px;
}

.empty-state-content {
  text-align: center;
  padding: 24px;
}

/* Dialog Styling */
.modern-dialog-card {
  border-radius: 12px !important;
  overflow: hidden !important;
}

.dialog-header {
  background: linear-gradient(135deg, #37cf37 0%, #2ba82b 100%);
  padding: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.dialog-icon-badge {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
}

.dialog-title {
  color: #ffffff;
  font-size: 20px;
  font-weight: 600;
}

.dialog-subtitle {
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  font-weight: 400;
}

/* Form Styling */
.form-section {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  border: 2px solid #e9ecef;
}

.form-section-header {
  display: flex;
  align-items: center;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
}

.field-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 8px;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .rules-grid {
    grid-template-columns: 1fr;
  }
  
  .rule-card {
    width: 100%;
  }
}
</style>

<style>
/* Override success chip colors in automation rules */
.rule-section .v-chip.success {
  background-color: #119f75 !important;
  border-color: #119f75 !important;
}

.rule-section .v-chip.success--text {
  color: #119f75 !important;
  caret-color: #119f75 !important;
}
</style>
